import { Fragment, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Leaf } from "lucide-react";

import { Gradients } from "components/gradients";
import { useGauge } from "hooks/use-gauge";
import { useStore } from "hooks/use-store";
import { useAnimatedValue } from "hooks/use-animated-value";
import { useFuelStatistics } from "hooks/use-fuel-statistics";
import { PLACEHOLDER } from "utils/constants";
import { Color } from "utils/theme";
import { opacity } from "utils/canvas";
import {
  DEFAULT_TICK_COLOR,
  MAIN_ARC_BG,
  MAIN_ARC_STROKE,
  OUTER_ARC_BG,
  OUTER_ARC_STROKE,
} from "components/gauge-style";
import { SvgGradients } from "types/enums";

// Constants
const START_ANGLE = 30;
const END_ANGLE = 330;
const MAX_VALUE = 60;
const DIAMETER = 450;
const ARC_WIDTH = 22;
const TICK_OFFSET = ARC_WIDTH + 10;
const TICK_WIDTH = 4;
const TICK_LENGTH = 15;
const STROKE_CAP = "round";
const MIN_SOG_TO_SHOW = 1;

export const EcoGauge = () => {
  const { t } = useTranslation();

  // Store values
  const sog = useStore((state) => state.sog);
  const ecoSog = useStore((state) => state.ecoSog);

  // Animated values for smooth transitions
  const animatedSog = useAnimatedValue(sog);

  // Gauge setup
  const gauge = useGauge({
    domain: [0, MAX_VALUE],
    startAngle: START_ANGLE,
    endAngle: END_ANGLE,
    numTicks: MAX_VALUE,
    diameter: DIAMETER,
  });

  // Fuel statistics
  const {
    fuelCurveStatusMessage,
    ecoSogSpeed,
    isValueInEcoRange,
    isInEcoRange,
  } = useFuelStatistics();

  // Determine tick color based on current SOG
  const getTickColor = useCallback(
    (tickValue: number, defaultColor: string) => {
      const inRange = isValueInEcoRange(tickValue);
      const tickInSogRange = sog !== null && tickValue <= sog;

      // In eco range and in SOG range
      if (inRange && tickInSogRange) return Color.Emerald[400];

      // In eco range
      if (inRange) return opacity(Color.Emerald[400], 0.4);

      // In SOG range
      if (tickInSogRange) return Color.Indigo[400];

      // Default tick color
      return defaultColor;
    },
    [sog, isValueInEcoRange]
  );

  return (
    <svg
      {...gauge.getSVGProps(ARC_WIDTH * 1.5)}
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-full"
    >
      {/* Defs */}
      <Gradients />

      {/* Main arc */}
      <g id="main-arc">
        <path
          id="main-arc-stroke"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 1.4}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-bg"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_BG}
          strokeWidth={ARC_WIDTH * 1.3}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-value"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle:
              sog !== null && sog > MAX_VALUE
                ? gauge.valueToAngle(MAX_VALUE)
                : gauge.valueToAngle(
                    animatedSog > MIN_SOG_TO_SHOW ? animatedSog : 0
                  ),
          })}
          fill="none"
          stroke={
            isInEcoRange
              ? `url(#${SvgGradients.Green})`
              : `url(#${SvgGradients.Blue})`
          }
          strokeWidth={animatedSog > 0 ? ARC_WIDTH : 0}
          strokeLinecap={STROKE_CAP}
        />
      </g>

      {/* Outer arc */}
      <g id="outer-arc">
        <path
          id="outer-arc-stroke"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.2,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 0.55}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="outer-arc-bg"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.2,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_BG}
          strokeWidth={ARC_WIDTH * 0.5}
          strokeLinecap={STROKE_CAP}
        />
        {ecoSog?.eco_ranges?.map((range, index) => {
          // skip ranges outside of gauge limits
          if (range.start > MAX_VALUE || range.end < MIN_SOG_TO_SHOW) {
            return null;
          }

          // clamp range to gauge limits
          const end = Math.min(range.end, MAX_VALUE);

          return (
            <path
              key={`outer-arc-value-1-${index}`}
              id={`outer-arc-value-1-${index}`}
              {...gauge.getArcProps({
                offset: ARC_WIDTH * 1.2,
                startAngle: gauge.valueToAngle(range.start),
                endAngle: gauge.valueToAngle(end),
              })}
              fill="none"
              stroke={`url(#${SvgGradients.Green})`}
              opacity={isInEcoRange ? 1 : 0.5}
              strokeWidth={ARC_WIDTH * 0.5}
              strokeLinecap={STROKE_CAP}
            />
          );
        })}
      </g>

      {/* Ticks */}
      <g id="ticks">
        {gauge.ticks.map((angle, index) => {
          const asValue = gauge.angleToValue(angle);
          const showText = asValue % 5 === 0;
          const tickColor = getTickColor(asValue, DEFAULT_TICK_COLOR);

          return (
            <Fragment key={`tick-${angle}-${index}`}>
              <line
                strokeWidth={TICK_WIDTH}
                stroke={tickColor}
                strokeLinecap={"round"}
                {...gauge.getTickProps({
                  angle,
                  length: showText ? TICK_LENGTH : TICK_LENGTH * 0.5,
                  offset: -TICK_OFFSET,
                })}
              />
              {showText && (
                <text
                  className="text-base font-medium font-mono"
                  fill={tickColor}
                  {...gauge.getLabelProps({ angle, offset: TICK_OFFSET * 1.9 })}
                >
                  {asValue}
                </text>
              )}
            </Fragment>
          );
        })}
      </g>

      {/* Center top label  */}
      <g id="center-top">
        {isInEcoRange && (
          <>
            <text
              y={"-20%"}
              x={5}
              className="text-xl font-semibold"
              fill={Color.Green[400]}
              alignmentBaseline="baseline"
              dominantBaseline="baseline"
              textAnchor="middle"
            >
              eco
            </text>
            <Leaf
              y={"-23%"}
              x={-38}
              alignmentBaseline="middle"
              dominantBaseline="middle"
              textAnchor="middle"
              className="inline-block"
              width={20}
              height={20}
              stroke={Color.Green[400]}
            />
          </>
        )}
      </g>

      {/* Center value and label */}
      <g id="center">
        <text
          y={"15%"}
          className="text-xl font-semibold uppercase"
          fill={isInEcoRange ? Color.Green[400] : Color.Primary[500]}
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="middle"
        >
          {t("dashboard.sog")}
        </text>
        <text
          fill={isInEcoRange ? Color.Green[400] : Color.White}
          className="text-9xl font-bold font-mono"
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="middle"
        >
          {sog !== null ? Math.round(sog) : PLACEHOLDER}
        </text>
      </g>

      {/* Center bottom label and value */}
      <g id="center-bottom">
        {fuelCurveStatusMessage ? (
          <foreignObject
            x={"-19%"}
            y={"28%"}
            className="text-base font-medium text-center p-2"
            color={Color.Primary[400]}
            width={200}
            height={100}
          >
            <p>{fuelCurveStatusMessage}</p>
          </foreignObject>
        ) : (
          <>
            <text
              y={"45%"}
              className="text-lg font-semibold"
              fill={isInEcoRange ? Color.Green[400] : Color.Primary[500]}
              alignmentBaseline="baseline"
              dominantBaseline="baseline"
              textAnchor="middle"
            >
              {t("fuel_curve.eco_speed")}
            </text>
            <text
              y={"38%"}
              fill={isInEcoRange ? Color.Green[400] : Color.White}
              className="text-7xl font-semibold font-mono"
              alignmentBaseline="baseline"
              dominantBaseline="baseline"
              textAnchor="middle"
            >
              {ecoSogSpeed !== null ? Math.round(ecoSogSpeed) : PLACEHOLDER}
            </text>
          </>
        )}
      </g>
    </svg>
  );
};
